import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api.js';
import AuthLayout from '../components/AuthLayout.jsx';
import Alert from '../components/ui/Alert.jsx';
import { Loading } from '../components/ui/index.jsx';

/**
 * The page behind the emailed approval link. Opening it changes nothing (mail filters
 * open every link to scan it); only pressing a button decides. No account needed.
 */
export default function ApproveDomain() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState('');

  useEffect(() => {
    api(`/public/domain-approvals/${token}`).then((d) => setReq(d.request)).catch((err) => setError(err.message));
  }, [token]);

  const decide = async (decision) => {
    setBusy(decision); setError('');
    try {
      const res = await api(`/public/domain-approvals/${token}`, { method: 'POST', body: { decision } });
      setReq(res.request); setResult(res.result);
    } catch (err) {
      if (err.data?.request) setReq(err.data.request);
      setError(err.message);
    }
    setBusy('');
  };

  if (error && !req) {
    return <AuthLayout><h1>Domain approval</h1><Alert tone="danger">{error}</Alert></AuthLayout>;
  }
  if (!req) return <AuthLayout><Loading /></AuthLayout>;

  const who = req.requested_by ? `${req.requested_by} (${req.requested_by_email})` : req.requested_by_email;
  const open = req.status === 'pending';

  return (
    <AuthLayout onSubmit={(e) => e.preventDefault()}>
      <h1>{req.status === 'approved' ? 'Thank you — approved' : req.status === 'declined' ? 'Thank you — declined' : <>Does {req.domain} belong to {req.organisation_name}?</>}</h1>
      {error && <Alert tone="danger">{error}</Alert>}

      {open && (
        <>
          <p><strong>{who}</strong> has asked SummitShield to record <strong className="mono">{req.domain}</strong> as{' '}
            <strong>{req.organisation_name}</strong>&rsquo;s official domain. This email was sent to you at <span className="mono">{req.approver_email}</span>.</p>
          <p className="muted">SummitShield lets attendees and exhibitors check whether an email or website really comes from the organiser.
            Only approve this if you work for {req.organisation_name} and this is right.</p>
          <div className="decision-buttons">
            <button type="button" className="btn btn-primary btn-block" disabled={!!busy} onClick={() => decide('approve')}>
              {busy === 'approve' ? 'Approving…' : `Yes, ${req.domain} is ours`}
            </button>
            <button type="button" className="btn btn-secondary btn-block" disabled={!!busy} onClick={() => decide('decline')}>
              {busy === 'decline' ? 'Saving…' : 'I don’t recognise this'}
            </button>
          </div>
        </>
      )}

      {req.status === 'approved' && (
        <Alert tone="success" title="Thank you — approved.">
          {result ? `${req.organisation_name} can now use ${req.domain} on SummitShield.` : 'This request has already been approved.'} You can close this page.
        </Alert>
      )}
      {req.status === 'declined' && (
        <Alert tone="info" title="Thanks for letting us know.">
          Nothing has been approved. We&rsquo;ve cancelled the request and our team will look into it. You can close this page.
        </Alert>
      )}
      {['expired', 'cancelled', 'failed'].includes(req.status) && (
        <Alert tone="warn">
          {req.status === 'expired' ? 'This link has expired.' : req.status === 'failed' ? 'This request couldn’t be completed.' : 'This link has been replaced by a newer one.'}
          {' '}Nothing has been approved. If this is right, ask {req.requested_by || 'them'} to send a new one.
        </Alert>
      )}
      <p className="muted small">SummitShield will never ask you for a password, a code or a payment on this page.</p>
    </AuthLayout>
  );
}
